import React, { useState } from 'react';
import { Upload, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../config/axios';

const ImportData = () => {
  const [jsonData, setJsonData] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [isValidJson, setIsValidJson] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('Palm Coast');

  const validateJson = (data) => {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object';
    } catch (error) {
      return false;
    }
  };

  const handleJsonChange = (e) => {
    const data = e.target.value;
    setJsonData(data);
    
    if (data.trim()) {
      const isValid = validateJson(data);
      setIsValidJson(isValid);
      
      if (isValid) {
        try {
          const parsed = JSON.parse(data);
          setParsedData(parsed);
        } catch (error) {
          setParsedData(null);
        }
      } else {
        setParsedData(null);
      }
    } else {
      setIsValidJson(false);
      setParsedData(null);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setJsonData(content);
      handleJsonChange({ target: { value: content } });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!isValidJson || !parsedData) {
      toast.error('Please provide valid JSON data');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/import/json', {
        data: parsedData,
        region: selectedRegion
      });

      setImportResults(response.data);
      toast.success('Data imported successfully!');
      setShowPreview(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setJsonData('');
    setParsedData(null);
    setIsValidJson(false);
    setImportResults(null);
    setShowPreview(false);
  };

  const getCompanyTypeBadge = (type) => {
    const typeColors = {
      company: 'badge-primary',
      supplier: 'badge-secondary',
      customer: 'badge-success'
    };

    return <span className={`badge ${typeColors[type] || 'badge-secondary'}`}>{type}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Import Data</h1>
        <p className="page-subtitle">
          Import company and contact data by pasting JSON or uploading a file
        </p>
      </div>

      {/* Import Form */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Import Data</h2>
        </div>
        <div className="card-body">
          {/* File Upload */}
          <div className="mb-4">
            <label className="form-label">Upload JSON File (Optional)</label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="form-input"
            />
          </div>

          {/* JSON Input */}
          <div className="mb-4">
            <label className="form-label">
              Paste JSON Data
              {isValidJson && (
                <span className="ml-2 text-green-600 text-sm">✓ Valid JSON</span>
              )}
              {jsonData && !isValidJson && (
                <span className="ml-2 text-red-600 text-sm">✗ Invalid JSON</span>
              )}
            </label>
            <textarea
              className="form-textarea"
              rows="12"
              placeholder="Paste your JSON data here..."
              value={jsonData}
              onChange={handleJsonChange}
            />
          </div>

          {/* Region Selection */}
          <div className="mb-4">
            <label className="form-label">Select Region</label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="form-select"
            >
              <option value="Palm Coast">Palm Coast</option>
              <option value="Orlando">Orlando</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              All imported companies will be assigned to the selected region
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleImport}
              disabled={!isValidJson || loading}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Data
                </>
              )}
            </button>
            
            <button
              onClick={handleClear}
              disabled={!jsonData}
              className="btn btn-outline"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </button>

            {parsedData && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="btn btn-secondary"
              >
                {showPreview ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Preview
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Preview
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Data Preview */}
      {showPreview && parsedData && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">
              Data Preview ({parsedData.searchResults?.length || 0} items)
            </h3>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Type</th>
                    <th>Country</th>
                    <th>Address</th>
                    <th>Shipments</th>
                    <th>Recent Shipment</th>
                    <th>Suppliers</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.searchResults?.slice(0, 10).map((item, index) => (
                    <tr key={index}>
                      <td className="font-medium">{item.title}</td>
                      <td>{getCompanyTypeBadge(item.type)}</td>
                      <td>{item.countryCode}</td>
                      <td className="max-w-xs truncate" title={item.address}>
                        {item.address}
                      </td>
                      <td>{item.totalShipments || 0}</td>
                      <td>{item.mostRecentShipment || 'N/A'}</td>
                      <td>
                        {item.topSuppliers?.slice(0, 2).join(', ')}
                        {item.topSuppliers?.length > 2 && '...'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedData.searchResults?.length > 10 && (
                <p className="text-sm text-gray-500 mt-2">
                  Showing first 10 items of {parsedData.searchResults.length} total
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Results */}
      {importResults && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Import Results</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Companies</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="font-medium text-green-600">
                      {importResults.results.companies.created}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Updated:</span>
                    <span className="font-medium text-blue-600">
                      {importResults.results.companies.updated}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Errors:</span>
                    <span className="font-medium text-red-600">
                      {importResults.results.companies.errors}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Suppliers</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="font-medium text-green-600">
                      {importResults.results.suppliers.created}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Errors:</span>
                    <span className="font-medium text-red-600">
                      {importResults.results.suppliers.errors}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Processed:</span>
                <span className="font-medium text-gray-900">
                  {importResults.totalProcessed}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Import Format Help</h3>
        </div>
        <div className="card-body">
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-600 mb-3">
              The system expects JSON data in the following format:
            </p>
            <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`{
  "searchResults": [
    {
      "title": "Company Name",
      "type": "company|supplier|customer",
      "countryCode": "US",
      "address": "Company Address",
      "totalShipments": 100,
      "mostRecentShipment": "2024-01-15",
      "topSuppliers": ["Supplier 1", "Supplier 2"],
      "url": "company-url"
    }
  ]
}`}
            </pre>
            <p className="text-gray-600 mt-3">
              <strong>Note:</strong> The system will automatically create companies and their associated suppliers.
              Existing companies with the same name will be updated with new information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportData;
